import { dataService } from '@app/modules/data';
import { DataObject } from './base';

import { Session } from './session';
import { PrintDevice } from './printer';
import { Place } from './place';
import { QrCode } from './qrcode';

export interface _Event {
    created: Date,
    target: string,
};

abstract class RegistryEvent extends DataObject {

    constructor(data: dataService){
        super('EVENT', null, data, null);
    }

    /****************************/
    /* DATA OBJECT ABSTRACTS    */
    /****************************/

    Copy(store: Array<DataObject> = []): RegistryEvent {
        return this._Copy(store) as RegistryEvent;
    }

    set Data(value){
        /* nothing to do */
    }

    set Info(value){
        /* nothing to do */
    }

    protected _OnUpdate(info){
        /* nothing to do */
    }

    /****************************/
    /* SERVER NOTIFICATION      */
    /****************************/

    Notify(){
        if (this.ToNotify){
            // Audit-log events (LOGIN / DRAWER / GUEST / RECEIPT) are
            // fire-and-forget: the caller never awaits the commit. Using
            // force=true here would trigger a blocking _ForceRefresh
            // round-trip to the server on every notification — ~3.6s on
            // login alone, which stalls the boot sequence right when the
            // PLACE view needs to hydrate. The event is still pushed to
            // the pending-changes queue (persisted in IndexedDB, survives
            // reload) and sent to the server on the next periodic sync
            // tick, so nothing is lost; just no blocking on the caller.
            this.DoCommit(false);
        }
        else {
            console.warn("[NOTIFY] Event not ready for notification!")
        }
    }
}

/**************************************/
/* OPEN DRAWER EVENT                  */
/**************************************/

export interface _DrawerEventData extends _Event {
    printer: number,
    waiter: number      // objid de USER del camarero EFECTIVO (igual que TICKET.waiter)
}

export class DrawerEvent extends RegistryEvent {
    protected _event: _DrawerEventData = {
        created: null,
        target: null,
        printer: null,
        waiter: null
    };

    constructor(data: dataService){
        super(data);

        this._event.created = new Date();
        this._event.target = 'DRAWER';
    }

    get Info(){
        return this._event;
    }

    /****************************/
    /* CLASS MEMBERS            */
    /****************************/

    get printer(): PrintDevice {
        return this._children['printer'] || null;
    }

    set printer(value: PrintDevice){
        if (this.SetChild('printer', value, 'printer')){
            this.ToNotify = true;
        }
    }

    // Camarero EFECTIVO (objid de USER) que solicitó la acción. Lo rellena data.ts con el
    // usuario efectivo (camarero del PIN / owner), no con la cuenta de sesión. Mismo tipo
    // que TICKET.waiter, para que la columna CAMARERO del informe resuelva igual.
    get waiter(): number {
        return this._event.waiter;
    }
    set waiter(value: number){
        this.patchValue(this._event, 'waiter', value || null);
    }

    /****************************/
    /* COMMIT OPERATION         */
    /****************************/

    protected get Change() {
        return {
            created: this._event.created ? this.dateStrToMysql(this._event.created) : null,
            target: this._event.target,
            printer: this._event.printer,
            waiter: this._event.waiter
        };
    }

    protected get Depend() {
        return {
            printer: { item: this.printer, relation_info: { to: null, by: 'printer' } }         // no relation to this in this[by -> 'printer']
        };
    }

    protected get Children(){
        return [ /* empty */ ];
    }
}

/**************************************/
/* USER LOGIN/LOGOUT EVENT            */
/**************************************/

export interface _LoginEventData extends _Event {
    place: number,
    login: boolean
}

export class LoginEvent extends RegistryEvent {
    protected _event: _LoginEventData = {
        created: null,
        target: null,
        place: null,
        login: null,
    };

    constructor(data: dataService){
        super(data);

        this._event.created = new Date();
        this._event.target = 'LOGIN';
    }    

    get Info(){
        return this._event;
    }

    /****************************/
    /* CLASS MEMBERS            */
    /****************************/

    private get _isReady(){
        if (this._event.place === null){
            return false;
        }

        if (this._event.login === null){
            return false;
        }

        return true;
    }

    get place(): Place {
        return this._children['place'] || null;
    }

    set place(value: Place){
        if (this.SetChild('place', value, 'place')){
            this.ToNotify = this._isReady;
        }
    }

    get login(): boolean {
        return this._event.login;
    }

    set login(value: boolean) {
        if (this.patchValue(this._event, 'login', value)){
            this.ToNotify = this._isReady;
        }        
    }

    /****************************/
    /* COMMIT OPERATION         */
    /****************************/

    protected get Change() {
        return {
            created: this._event.created ? this.dateStrToMysql(this._event.created) : null,
            target: this._event.target,
            place: this._event.place,
            login: this._event.login ? '1' : '0',
        };
    }

    protected get Depend() {
        return {
            place: { item: this.place, relation_info: { to: null, by: 'place' } }         // no relation to this in this[by -> 'place']
        };
    }

    protected get Children(){
        return [ /* empty */ ];
    }
}

/**************************************/
/* GUEST LOGIN/LOGOUT EVENT           */
/**************************************/

export interface _GuestEventData extends _Event {
    qrcode: number,
    login: boolean
}

export class GuestEvent extends RegistryEvent {
    protected _event: _GuestEventData = {
        created: null,
        target: null,
        qrcode: null,
        login: null,
    };

    constructor(data: dataService){
        super(data);

        this._event.created = new Date();
        this._event.target = 'GUEST';
    }    

    get Info(){
        return this._event;
    }

    /****************************/
    /* CLASS MEMBERS            */
    /****************************/

    private get _isReady(){
        if (this._event.qrcode === null){
            return false;
        }

        if (this._event.login === null){
            return false;
        }

        return true;
    }

    get qrcode(): QrCode {
        return this._children['qrcode'] || null;
    }

    set qrcode(value: QrCode){
        if (this.SetChild('qrcode', value, 'qrcode')){
            this.ToNotify = this._isReady;
        }
    }

    get login(): boolean {
        return this._event.login;
    }

    set login(value: boolean) {
        if (this.patchValue(this._event, 'login', value)){
            this.ToNotify = this._isReady;
        }        
    }

    /****************************/
    /* COMMIT OPERATION         */
    /****************************/

    protected get Change() {
        return {
            created: this._event.created ? this.dateStrToMysql(this._event.created) : null,
            target: this._event.target,
            qrcode: this._event.qrcode,
            login: this._event.login ? '1' : '0',
        };
    }

    protected get Depend() {
        return {
            qrcode: { item: this.qrcode, relation_info: { to: null, by: 'qrcode' } }         // no relation to this in this[by -> 'qrcode']
        };
    }

    protected get Children(){
        return [ /* empty */ ];
    }
}

/**************************************/
/* TICKET PRINTING EVENT              */
/**************************************/

export interface _ReceiptEventData extends _Event {
    place: number,
    ticketinfo: any,
    ticket: number,     // objid del TICKET impreso: el server resuelve TICKET.waiter (como e0)
    waiter: number      // objid de USER del camarero EFECTIVO (fallback si no hay ticket objid)
}

export class ReceiptEvent extends RegistryEvent {
    protected _event: _ReceiptEventData = {
        created: null,
        target: null,
        place: null,
        ticketinfo: null,
        ticket: null,
        waiter: null
    };

    constructor(data: dataService){
        super(data);

        this._event.created = new Date();
        this._event.target = 'RECEIPT';
    }    

    get Info(){
        return this._event;
    }

    /****************************/
    /* CLASS MEMBERS            */
    /****************************/

    private get _isReady(){
        if (this._event.place === null){
            return false;
        }

        if (this._event.ticketinfo === null){
            return false;
        }

        return true;
    }


    get place(): Place {
        return this._children['place'] || null;
    }

    set place(value: Place){
        if (this.SetChild('place', value, 'place')){
            this.ToNotify = this._isReady;
        }
    }

    get ticketinfo(): any {
        return this._event['ticketinfo']
    }

    set ticketinfo(value: QrCode){
        if (this.patchValue(this._event, 'ticketinfo', value)){        
            this.ToNotify = this._isReady;
        }
    }

    get charge(): number {
        if (this.ticketinfo){
            return this.ticketinfo['total'] || null;
        }
        return null;
    }

    // Camarero EFECTIVO (objid de USER) que imprimió. Igual que TICKET.waiter.
    get waiter(): number {
        return this._event.waiter;
    }
    set waiter(value: number){
        this.patchValue(this._event, 'waiter', value || null);
    }

    // objid del TICKET impreso: el server resuelve TICKET.waiter server-side (como el
    // evento e0), para que la atribución sea idéntica y no dependa del estado del cliente.
    get ticket(): number {
        return this._event.ticket;
    }
    set ticket(value: number){
        this.patchValue(this._event, 'ticket', value || null);
    }

    /****************************/
    /* COMMIT OPERATION         */
    /****************************/

    protected get Change() {
        return {
            created: this._event.created ? this.dateStrToMysql(this._event.created) : null,
            target: this._event.target,
            place: this._event.place,
            charge: this.charge,
            ticket: this._event.ticket,
            waiter: this._event.waiter,
            custom: JSON.stringify(this._event.ticketinfo)
        };
    }

    protected get Depend() {
        return {
            place: { item: this.place, relation_info: { to: null, by: 'place' } }         // no relation to this in this[by -> 'place']
        };
    }

    protected get Children(){
        return [ /* empty */ ];
    }
}



